import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { sideBar } from "../../utils/AgGridConfig";
import QuickSearch from "../../components/QuickSearch";
import { Shareholder } from "../../utils/stage3types";
import {
  SecurityTypeRender,
  ShareholderNameRender,
  ShareholderTypeRender,
  validateInvestments,
} from "./ShareholdersAGTableComponents";
import {
  useGetMcaProcessedPreSignedURL,
  useGetPreSignedURL,
  useUpdateCompanyDetails,
} from "../../queries/onboarding";
import { SecondaryCTAButton } from "../../components/CTAButtonComponents";
import { VStack } from "../../components/utils";

export const securityType = [
  "EQUITY",
  "PREFERENCE",
  "CCD",
  "OCD",
  "NOTE",
  "WARRANT",
  "RPS",
  "NCD",
  "OCPS",
  "OCRPS",
  "DEBENTURE",
  "OPTIONS",
  "CCPS",
  "FORFEITED",
];

export const shareHolderTypes = [
  "Founders",
  "Angels",
  "Incubator",
  "Accelerator",
  "Angel Network",
  "Angel Fund",
  "VC",
  "Corporate",
  "Family Office",
  "PE",
  "Financial Institution",
  "Options",
  "Others",
  "Employee",
  "Trust",
  "ESOP Trust",
  "Forfeited",
  "HNI",
];

export const ShareholderAGTable: React.FC<{
  onClose: Function;
  id: string;
  shareholdersData: Shareholder[];
  cin: string;
  selectedMgt7: string;
  loaUrl?: string;
}> = ({ onClose, id, shareholdersData, cin, selectedMgt7, loaUrl }) => {
  const gridApi = useRef<any>(null);
  const { mutate: updateShareholderDetails } = useUpdateCompanyDetails();
  const [isSignedUrl, setIsSignedUrl] = useState<string>();
  const [isProcessedSignedUrl, setIsProcessedSignedUrl] = useState<string>();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState<any>();
  const [selectedData, setSelectedData] = useState<any>();
  const { data: signedUrl, refetch } = useGetPreSignedURL(
    isSignedUrl || "",
    isSignedUrl !== undefined,
  );

  useEffect(() => {
    if (!isSignedUrl) return;

    const fetchSignedUrl = async () => {
      try {
        const result = await refetch();
        if (result.data) {
          window.open(result.data, "_blank");
        }
      } catch (error) {
        toast("Error Downloading File", { type: "error", autoClose: 2000 });
      }
    };

    fetchSignedUrl();
  }, [isSignedUrl]);

  const { data: signedUrl1, refetch: refetchLoa } =
    useGetMcaProcessedPreSignedURL(
      isProcessedSignedUrl || "",
      isProcessedSignedUrl !== undefined,
    );

  useEffect(() => {
    if (!isProcessedSignedUrl) return;

    const fetchSignedUrl = async () => {
      try {
        const result = await refetchLoa();
        if (result.data) {
          window.open(result.data, "_blank");
        }
      } catch (error) {
        toast("Error Downloading File", { type: "error", autoClose: 2000 });
      }
    };

    fetchSignedUrl();
  }, [isProcessedSignedUrl]);

  const [updatedData, setUpdatedData] = React.useState<any[]>(shareholdersData);
  const data: Shareholder[] = shareholdersData;

  const componentsRegistry = useMemo(
    () => ({
      shareholderName: memo(ShareholderNameRender),
      securityType: memo(SecurityTypeRender),
      shareholderType: memo(ShareholderTypeRender),
    }),
    [],
  );

  useEffect(() => {
    if (!showError) return;
    const { isValid, errors } = validateInvestments(updatedData);
    setError(errors);
  }, [showError, updatedData]);

  function ShowConfirmPopup(data: any) {
    return (
      <Transition show={showConfirm} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setShowConfirm(false)}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                  <span className="indicator-progress text-sm font-semibold">
                    Are you sure you want to Remove this shareholder? This
                    action cannot be undone.
                  </span>
                  <div className="pt-8 flex justify-between sm:flex sm:flex-row-reverse">
                    <button
                      onClick={() => {
                        setUpdatedData(
                          updatedData.filter(
                            (d: any) =>
                              d.shareholderName !== data.data.shareholderName,
                          ),
                        );
                        setShowConfirm(false);
                      }}
                      className={`px-4 py-1 text-sm text-white cursor-pointer bg-blue-800 hover:bg-blue-900 rounded `}
                    >
                      Remove
                    </button>

                    <button
                      onClick={() => setShowConfirm(false)}
                      className={`px-4 py-1 text-sm cursor-pointer border border-gray-800 text-gray-900 rounded `}
                    >
                      Cancel
                    </button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }
  function ShowErrorPopup(errors: any) {
    return (
      <Transition show={showError} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setShowError(false)}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="w-3/6 h-[400px] transform overflow-auto rounded-lg bg-white p-6 shadow-xl transition-all">
                  {errors && (
                    <>
                      {errors?.length > 0 && (
                        <>
                          <p className="text-base text-gray-700 font-semibold text-left underline">
                            Errors
                          </p>
                          <ul className="list-disc pl-5 text-base font-semibold text-gray-800 text-left h-[265px] overflow-auto">
                            {errors.map((item: string, index: number) => {
                              const trimmed = item.trim();
                              return trimmed ? (
                                <li key={index}>{trimmed}.</li>
                              ) : null;
                            })}
                          </ul>
                        </>
                      )}
                      {!errors?.length && (
                        <VStack className="w-full mx-auto placeholderLoader h-[290px]">
                          <img
                            src="/emptyTable.png"
                            className="h-44 my-5 w-56 mx-auto placeholderLoader"
                            alt="no data"
                          />
                          <p className="mx-auto text-base font-semibold text-labelColor ">
                            {
                              "No errors found. You can proceed with the submission."
                            }
                          </p>
                        </VStack>
                      )}
                    </>
                  )}

                  <div className="pt-8 flex justify-end sm:flex gap-4">
                    <button
                      onClick={() => setShowError(false)}
                      className="px-4 py-1 text-sm cursor-pointer border border-gray-800 text-gray-900 rounded"
                    >
                      Close
                    </button>
                    <button
                      disabled={errors?.length > 0}
                      onClick={() => {
                        handleSave();
                      }}
                      className={`px-4 py-1 text-sm text-white  ${errors?.length > 0 ? " cursor-not-allowed bg-blue-300" : " bg-blue-700 hover:bg-blue-800"} rounded `}
                    >
                      Submit
                    </button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  function handleSave() {
    updateShareholderDetails(
      {
        shareholders: updatedData as Shareholder[],
        processedDataId: id,
        cin,
      },
      {
        onSuccess: () => {
          toast("Excel file regenerated successfully", {
            autoClose: 2000,
            type: "success",
          });
          onClose();
        },
        onError: (error: any) => {
          toast(error?.response?.data?.errorMessage, {
            type: "error",
            autoClose: 2000,
          });
          onClose();
        },
      },
    );
  }

  function DeleteButtonRender(data: any) {
    return (
      <div>
        {data.data?.shareholderName !== "Total" && (
          <p className="font-semibold truncate mt-4">
            <Trash2
              className="w-4 h-4 text-red-500 hover:text-red-800 cursor-pointer"
              onClick={() => {
                setSelectedData(data);
                setShowConfirm(true);
              }}
            />
          </p>
        )}
      </div>
    );
  }

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "",
        field: "number",
        pinned: "left",
        width: 60,
        maxWidth: 60,
        filter: false,
        suppressMenu: true,
      },
      {
        headerName: "Shareholder Name",
        field: "shareholderName",
        filter: "agTextColumnFilter",
        sortable: true,
        minWidth: 250,
        cellClass: "with-left-border",
        headerClass: "with-left-border",
        pinned: "left",
        cellRenderer: "shareholderName",
        editable: true,
        tooltipField: "shareholderNameTooltip",
      },
      {
        headerName: "Security Type",
        field: "securityType",
        filter: "agSetColumnFilter",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
          values: securityType,
        },
        sortable: true,
        cellRenderer: "securityType",
        editable: true,
        tooltipField: "securityTypeTooltip",
      },
      {
        headerName: "Security Class",
        field: "securityClass",
        filter: "agSetColumnFilter",
        sortable: true,
        editable: true,
        cellRenderer: "securityClass",
        tooltipField: "securityClassTooltip",
      },
      {
        headerName: "Shareholder Type",
        field: "shareholderType",
        filter: "agSetColumnFilter",
        minWidth: 160,
        editable: true,
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
          values: shareHolderTypes,
        },
        sortable: true,
        cellRenderer: "shareholderType",
      },
      {
        headerName: "Non-Priced",
        field: "nonPriced",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
          values: ["Yes", "No"],
        },
        minWidth: 130,
        editable: true,
        filter: "agSetColumnFilter",
        sortable: true,
        cellRenderer: "nonPriced",
      },
      {
        headerName: "Number Of Shares",
        field: "numberOfShares",
        minWidth: 160,
        editable: true,
        filter: "agTextColumnFilter",
        sortable: true,
        cellRenderer: "numberOfShares",
        tooltipField: "numberOfSharesTooltip",
      },
      {
        headerName: "Shareholder FDB %",
        field: "shareholderFDBPercentage",
        editable: true,
        minWidth: 160,
        filter: "agTextColumnFilter",
        sortable: true,
        cellRenderer: "shareholderFDBPercentage",
        tooltipField: "shareholderFDBPercentageTooltip",
      },
      {
        headerName: "Date of Investment",
        field: "dateOfInvestment",
        editable: true,
        sortable: true,
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (cellValue == null) {
              return 0;
            }
            // const cellDateValue = cellValue.split('T')[0];
            const dateParts = cellValue.split("-");
            const day = Number(dateParts[2]);
            const month = Number(dateParts[1]) - 1;
            const year = Number(dateParts[0]);
            const cellDate = new Date(year, month, day);
            if (cellDate < dateFromFilter) {
              return -1;
            } else if (cellDate > dateFromFilter) {
              return 1;
            }
            return 0;
          },
        },
        cellRenderer: "dateOfInvestment",
      },
      {
        headerName: "Price per share",
        field: "pricePerShare",
        filter: "agTextColumnFilter",
        sortable: true,
        editable: true,
        cellRenderer: "pricePerShare",
        tooltipField: "ppsTooltip",
      },
      {
        headerName: "Shareholder Country",
        minWidth: 160,
        editable: true,
        field: "shareholderCountry",
        filter: "agSetColumnFilter",
        sortable: true,
        hide: true,
        cellRenderer: "shareholderCountry",
      },
      // {
      //   headerName: "Investment Currency",
      //   field: "invCurrency",
      //   filter: "agNumberColumnFilter",
      //   editable: true,
      //   sortable: true,
      //   cellRenderer: "invCurrency",
      // },
      // {
      //   headerName: "Exchange Rate",
      //   field: "exchangeRate",
      //   filter: "agTextColumnFilter",
      //   editable: true,
      //   sortable: true,
      //   cellRenderer: "exchangeRate",
      //   tooltipField: "exchangeRateTooltip",
      // },

      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 50,
        maxWidth: 50,
        suppressMenu: true,
        filter: false,
        menuTabs: [],
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        // cellStyle: { "padding-top": "12px", "line-height": "18px" },
        resizable: false,
        sortable: false,
        cellRendererParams: ({ value }: { value: any }) => value?.props,
        // cellRenderer: SubmitButtonRender,
        cellRenderer: DeleteButtonRender,
      },
    ],
    [],
  );
  function addNewRow() {
    if (!gridApi.current) return;
    const newItems = [
      {
        shareholderName: "",
        securityType: "EQUITY",
        securityClass: "",
        numberOfShares: 1,
        shareholderFDBPercentage: 1,
        shareholderType: "Others",
        dateOfInvestment: new Date().toISOString().split("T")[0],
        pricePerShare: "",
        shareholderCountry: "India",
        nonPriced: "No",
        isNew: true,
      },
    ];
    const newIndex = gridApi.current.api.getDisplayedRowCount();
    gridApi.current.api.applyTransaction({ add: newItems, addIndex: 0 });
    handleCellValueChanged();
  }

  const recalculatePercentage = useCallback(() => {
    if (!gridApi.current) return;

    const totalNumberOfShares = updatedData?.reduce(
      (acc: number, data: any) => acc + (data.numberOfShares || 0),
      0,
    );

    const updatedRows = updatedData?.map((item: any) => {
      const shareholderFDBPercentage = totalNumberOfShares
        ? (item.numberOfShares / totalNumberOfShares) * 100
        : 0;
      return {
        ...item,
        shareholderFDBPercentage: Number(shareholderFDBPercentage.toFixed(2)),
      };
    });

    setUpdatedData(updatedRows);
  }, [updatedData]);

  const rowData = useMemo(
    () =>
      updatedData?.map((item: any, index: number) => ({
        id: item.id,
        number: index + 1,
        shareholderName: item.shareholderName,
        shareholderNameTooltip: item.shareholderName,
        securityType: item.securityType,
        securityTypeTooltip: item.securityType,
        securityClass: item.securityClass,
        securityClassTooltip: item.securityClass,
        numberOfShares: item.numberOfShares,
        numberOfSharesTooltip: item.numberOfShares,
        shareholderFDBPercentage: item.shareholderFDBPercentage,
        shareholderFDBPercentageTooltip: item.shareholderFDBPercentage,
        shareholderType: item.shareholderType,
        shareholderTypeTooltip: item.shareholderType,
        dateOfInvestment: item.dateOfInvestment,
        pricePerShare: item.pricePerShare,
        pricePerShareTooltip: item.pricePerShare,
        shareholderCountry: item.shareholderCountry,
        nonPriced: item.nonPriced,
      })),
    [updatedData],
  );

  const handleCellValueChanged = () => {
    if (!gridApi.current) return;

    const updatedData: any[] = [];
    gridApi.current.api.forEachNode((node: any) => {
      updatedData.push(node.data);
    });
    setUpdatedData(updatedData);
  };

  const gridOptions = {
    suppressRowVirtualisation: true,
    paginationAutoPageSize: false,
    suppressScrollOnNewData: true,
  };

  const [pinnedBottomRowData, setPinnedBottomRowData] = useState<any[]>([]);

  const calculateTotals = useCallback((data: any[]) => {
    const totalNumberOfShares = data?.reduce(
      (acc: number, data: any) => acc + (data.numberOfShares || 0),
      0,
    );

    const totalFdbPercentage = data?.length
      ? data
          .reduce(
            (acc: number, data: any) =>
              acc + (data.shareholderFDBPercentage || 0),
            0,
          )
          .toFixed(2)
      : 0;

    return [
      {
        shareholderName: "Total",
        shareholderType: null,
        numberOfShares: totalNumberOfShares,
        numberOfSharesTooltip: totalNumberOfShares?.toLocaleString("en-IN"),
        shareholderFDBPercentage: Number(totalFdbPercentage),
        shareholderFDBPercentageTooltip: totalFdbPercentage,
      },
    ];
  }, []);

  const updatePinnedRow = useCallback(() => {
    if (!gridApi.current) return;

    const visibleData: any[] = [];
    gridApi.current.api.forEachNodeAfterFilter((node: any) => {
      if (node.data) visibleData.push(node.data);
    });

    const totals = calculateTotals(visibleData);
    setPinnedBottomRowData(totals);
  }, [calculateTotals]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    [],
  );

  return (
    <div className="h-[calc(100vh-12vh)] flex flex-col">
      <div className="flex flex-row justify-between">
        <div>
          <h2 className="text-lg text-secondary flex flex-row items-start font-semibold">
            Shareholder Details
          </h2>
          <p className="text-xs font-semibold text-gray-500">{`${updatedData?.length} Shareholders`}</p>
        </div>
        <div className="justify-end flex flex-row items-center gap-2">
          {loaUrl && loaUrl !== "" && (
            <div
              className="relative group cursor-pointer w-fit"
              onClick={() => setIsProcessedSignedUrl(loaUrl)}
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg"
                alt="File"
                className="w-8 h-8 object-contain"
              />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-200 text-black text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                {loaUrl?.split("/").pop() ?? "PDF File"}
              </div>
            </div>
          )}
          {selectedMgt7 && selectedMgt7 !== "" && (
            <div
              className="relative group cursor-pointer w-fit"
              onClick={() => setIsSignedUrl(selectedMgt7)}
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg"
                alt="File"
                className="w-8 h-8 object-contain"
              />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-200 text-black text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                {selectedMgt7?.split("/").pop() ?? "PDF File"}
              </div>
            </div>
          )}
          <QuickSearch
            placeholder={`Search`}
            className=""
            onChange={(e) => {
              gridApi.current.api.setGridOption(
                "quickFilterText",
                e.target.value,
              );
            }}
          />
          <X
            className="text-gray-500 hover:text-blue-500 cursor-pointer"
            onClick={() => {
              onClose();
            }}
          />
        </div>
      </div>
      {showConfirm && ShowConfirmPopup(selectedData)}
      {showError && ShowErrorPopup(error || [])}
      <div className="flex-1 overflow-y-auto mt-2">
        <div
          className="ag-theme-alpine pb-4 flex mt-2"
          style={{ height: "90%" }}
        >
          <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            components={componentsRegistry}
            ref={gridApi}
            // onGridReady={(params) => {
            //   gridApi.current = params;
            // }}
            // groupTotalRow={"bottom"} // Remove this line
            // rowSelection={rowSelection}
            // onSelectionChanged={() => {
            //   const selectedNodes = gridApi.current?.api.getSelectedNodes();
            //   const selectedData = selectedNodes?.map((node: any) => node.data);
            //   setSelectedRowData(selectedData);
            // }}
            alwaysMultiSort
            pinnedBottomRowData={pinnedBottomRowData}
            // onCellValueChanged={handleCellValueChanged}
            onGridReady={(params) => {
              gridApi.current = params;
              updatePinnedRow(); // initialize once grid is ready
            }}
            onCellValueChanged={updatePinnedRow}
            onFilterChanged={updatePinnedRow}
            onRowDataUpdated={updatePinnedRow}
            cellSelection={true}
            enableCellTextSelection={true}
            // grandTotalRow={"bottom"} // Remove this line
            defaultColDef={defaultColDef}
            className="w-full col"
            sideBar={sideBar}
            gridOptions={gridOptions}
            getRowStyle={(params) => {
              if (params.data?.isNew) {
                return { backgroundColor: "#fffbe6" }; // light yellow for new rows
              }
              if (params.rowIndex % 2 === 0) {
                return { backgroundColor: "#f8f8f8" };
              } else {
                return { backgroundColor: "#ffffff" };
              }
            }}
          />
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={() => {
              recalculatePercentage();
            }}
            className={`px-4 py-1 text-sm cursor-pointer border border-blue-800 text-blue-900 rounded `}
          >
            Re-calculate %
          </button>
          <button
            onClick={() => {
              addNewRow();
            }}
            className={`px-4 py-1 text-sm cursor-pointer border border-blue-800 text-blue-900 rounded `}
          >
            Add New
          </button>
          <button
            onClick={() => {
              setShowError(true);
            }}
            className={`px-4 py-1 text-sm text-white cursor-pointer bg-blue-800 hover:bg-blue-900 rounded `}
          >
            Validate
          </button>
        </div>
      </div>
    </div>
  );
};
